import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Upload, FileText, AlertCircle, CheckCircle, LoaderIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { submitForm } from '../utils/submitForm';
const MAX_FILE_SIZE = 48 * 1024 * 1024;
type CVFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: 'seafarer' | 'vacancy';
  vacancyTitle?: string;
  vacancyId?: string;
  vacancyType?: string;
};
export function CVFormModal({
  isOpen,
  onClose,
  mode,
  vacancyTitle,
  vacancyId,
  vacancyType
}: CVFormModalProps) {
  const {
    t,
    language
  } = useLanguage();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedRank, setSelectedRank] = useState('');
  const [otherRank, setOtherRank] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [yearsExp, setYearsExp] = useState('');
  const [availability, setAvailability] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const modalRef = useRef<HTMLDivElement>(null);
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setSelectedRank('');
    setOtherRank('');
    setShowOtherInput(false);
    setEmail('');
    setPhone('');
    setVesselType('');
    setYearsExp('');
    setAvailability('');
    setAgreedToTerms(false);
    setUploadedFile(null);
    setFileError('');
  };
  const rankOptions = [{
    category: t('specialists.form.ranks.deck'),
    options: [t('specialists.form.ranks.options.master'), t('specialists.form.ranks.options.chiefMate'), t('specialists.form.ranks.options.secondMate'), t('specialists.form.ranks.options.thirdMate'), t('specialists.form.ranks.options.bosun'), t('specialists.form.ranks.options.ab'), t('specialists.form.ranks.options.os')]
  }, {
    category: t('specialists.form.ranks.engine'),
    options: [t('specialists.form.ranks.options.chiefEng'), t('specialists.form.ranks.options.secondEng'), t('specialists.form.ranks.options.thirdEng'), t('specialists.form.ranks.options.fourthEng'), t('specialists.form.ranks.options.eto'), t('specialists.form.ranks.options.asstEto'), t('specialists.form.ranks.options.motorman'), t('specialists.form.ranks.options.wiper')]
  }, {
    category: t('specialists.form.ranks.other'),
    options: [t('specialists.form.ranks.options.dpo'), t('specialists.form.ranks.options.sdpo'), t('specialists.form.ranks.options.craneOp'), t('specialists.form.ranks.options.rigger'), t('specialists.form.ranks.options.electrician'), t('specialists.form.ranks.options.mechanic'), t('specialists.form.ranks.options.other')]
  }];
  const vesselTypes = [{
    value: 'tanker',
    label: t('specialists.form.vessels.tanker')
  }, {
    value: 'bulk',
    label: t('specialists.form.vessels.bulk')
  }, {
    value: 'container',
    label: t('specialists.form.vessels.container')
  }, {
    value: 'offshore',
    label: t('specialists.form.vessels.offshore')
  }, {
    value: 'cruise',
    label: t('specialists.form.vessels.cruise')
  }, {
    value: 'other',
    label: t('specialists.form.vessels.other')
  }];
  const validateFile = (file: File): boolean => {
    setFileError('');
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 48 MB limit');
      return false;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Invalid file type. Please upload PDF, DOC, DOCX, or image files');
      return false;
    }
    return true;
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);else if (e.type === 'dragleave') setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) setUploadedFile(file);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) setUploadedFile(file);
    }
  };
  const handleRankChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedRank(value);
    setShowOtherInput(value === t('specialists.form.ranks.options.other'));
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat' as const,
    backgroundPosition: 'right 12px center'
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms || submitting) return;
    // Validate required file upload
    if (!uploadedFile) {
      setFileError(t('specialists.form.labels.cvRequired') || 'Please upload your CV');
      return;
    }
    setSubmitting(true);
    setSubmitStatus('idle');
    const finalRank = showOtherInput ? otherRank : selectedRank;
    const vesselLabel = vesselTypes.find(v => v.value === vesselType)?.label || vesselType;
    // Build message
    const messageParts = [`Rank: ${finalRank}`];
    if (vesselLabel) messageParts.push(`Vessel exp: ${vesselLabel}`);
    if (yearsExp) messageParts.push(`Years: ${yearsExp}`);
    if (availability) messageParts.push(`Available: ${availability}`);
    const fd = new FormData();
    fd.append('name', `${firstName} ${lastName}`.trim());
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', messageParts.join(' | '));
    if (uploadedFile) {
      fd.append('file', uploadedFile, uploadedFile.name);
      fd.append('document', uploadedFile, uploadedFile.name);
      fd.append('cv', uploadedFile, uploadedFile.name);
    }
    // Vacancy-specific fields
    if (mode === 'vacancy') {
      if (vacancyTitle) fd.append('vacancyTitle', vacancyTitle);
      if (vacancyId) fd.append('vacancyId', vacancyId);
      if (vacancyType) fd.append('vacancyType', vacancyType);
    }
    try {
      const result = await submitForm('resume', fd, language);
      if (result.ok) {
        setSubmitStatus('success');
        resetForm();
        setTimeout(() => {
          setSubmitStatus('idle');
          onClose();
        }, 2500);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 6000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 6000);
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(<div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-238">
      <div ref={modalRef} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-8" role="dialog" aria-modal="true" data-id="element-239">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between" data-id="element-240">
          <div data-id="element-241">
            <h2 className="text-xl font-bold text-[var(--color-text-dark)]" data-id="element-242">
              {mode === 'vacancy' && vacancyTitle ? vacancyTitle : t('specialists.form.title')}
            </h2>
            {mode === 'vacancy' && vacancyType && <p className="text-sm text-[var(--color-text-light)] mt-0.5" data-id="element-243">
                {vacancyType}
              </p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-244">
            <X className="w-5 h-5 text-gray-500" data-id="element-245" />
          </button>
        </div>

        {/* Success overlay */}
        {submitStatus === 'success' && <div className="absolute inset-0 z-20 bg-white/95 rounded-2xl flex flex-col items-center justify-center gap-3" data-id="element-246">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center" data-id="element-247">
              <CheckCircle className="w-8 h-8 text-green-600" data-id="element-248" />
            </div>
            <p className="text-lg font-semibold text-[var(--color-text-dark)]" data-id="element-249">
              {t('formStatus.sent')}
            </p>
          </div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-250">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-251">
            <div data-id="element-252">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-253">
                {t('specialists.form.labels.firstName')}{' '}
                <span className="text-red-500" data-id="element-254">*</span>
              </label>
              <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-255" />
            </div>
            <div data-id="element-256">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-257">
                {t('specialists.form.labels.lastName')}{' '}
                <span className="text-red-500" data-id="element-258">*</span>
              </label>
              <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-259" />
            </div>
          </div>

          <div data-id="element-260">
            <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-261">
              {t('specialists.form.labels.rank')}{' '}
              <span className="text-red-500" data-id="element-262">*</span>
            </label>
            <select required value={selectedRank} onChange={handleRankChange} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-263">
              <option value="" data-id="element-264">
                {t('specialists.form.labels.selectRank')}
              </option>
              {rankOptions.map(group => <optgroup key={group.category} label={group.category} data-id="element-265">
                  {group.options.map(option => <option key={option} value={option} data-id="element-266">
                      {option}
                    </option>)}
                </optgroup>)}
            </select>
          </div>

          {showOtherInput && <div data-id="element-267">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-268">
                {t('specialists.form.labels.specify')}{' '}
                <span className="text-red-500" data-id="element-269">*</span>
              </label>
              <input type="text" required value={otherRank} onChange={e => setOtherRank(e.target.value)} placeholder={t('specialists.form.labels.enterPosition')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-270" />
            </div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-271">
            <div data-id="element-272">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-273">
                {t('specialists.form.labels.email')}{' '}
                <span className="text-red-500" data-id="element-274">*</span>
              </label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-275" />
            </div>
            <div data-id="element-276">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-277">
                {t('specialists.form.labels.phone')}{' '}
                <span className="text-red-500" data-id="element-278">*</span>
              </label>
              <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-279" />
              <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-280">
                {t('specialists.form.labels.phoneHint')}
              </p>
            </div>
          </div>

          {/* CV Upload */}
          <div data-id="element-281">
            <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-282">
              {t('specialists.form.labels.cv')}{' '}
              <span className="text-red-500" data-id="element-283">*</span>
            </label>
            {!uploadedFile ? <div data-id="element-284">
                <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => document.getElementById('modal-cv-upload')?.click()} className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${dragActive ? 'border-[var(--color-tan)] bg-[var(--color-tan)]/5' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-285">
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-286" />
                  <p className="text-sm text-[var(--color-text-dark)] mb-1" data-id="element-287">
                    {t('specialists.form.labels.dragDrop')}
                  </p>
                  <p className="text-xs text-[var(--color-text-light)]" data-id="element-288">
                    {t('specialists.form.labels.fileTypes')}
                  </p>
                  <input id="modal-cv-upload" type="file" accept=".pdf,.doc,.docx,image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleFileChange} className="hidden" data-id="element-289" />
                </div>
                {fileError && <div className="mt-2 flex items-start gap-2 text-red-500 text-sm" data-id="element-290">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-291" />
                    <span data-id="element-292">{fileError}</span>
                  </div>}
              </div> : <div className="border-2 border-[var(--color-tan)] bg-[var(--color-tan)]/5 rounded-lg p-4 flex items-center justify-between" data-id="element-293">
                <div className="flex items-center gap-3" data-id="element-294">
                  <FileText className="w-8 h-8 text-[var(--color-tan)]" data-id="element-295" />
                  <div data-id="element-296">
                    <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-297">
                      {uploadedFile.name}
                    </p>
                    <p className="text-xs text-[var(--color-text-light)]" data-id="element-298">
                      {formatFileSize(uploadedFile.size)}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => {
              setUploadedFile(null);
              setFileError('');
            }} className="p-2 hover:bg-red-50 rounded-full transition-colors" data-id="element-299">
                  <X className="w-5 h-5 text-red-500" data-id="element-300" />
                </button>
              </div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-301">
            <div data-id="element-302">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-303">
                {t('specialists.form.labels.vesselType')}{' '}
                <span className="text-red-500" data-id="element-304">*</span>
              </label>
              <select required value={vesselType} onChange={e => setVesselType(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-305">
                <option value="" data-id="element-306">
                  {t('specialists.form.labels.selectVessel')}
                </option>
                {vesselTypes.map(type => <option key={type.value} value={type.value} data-id="element-307">
                    {type.label}
                  </option>)}
              </select>
            </div>
            <div data-id="element-308">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-309">
                {t('specialists.form.labels.yearsExp')}{' '}
                <span className="text-red-500" data-id="element-310">*</span>
              </label>
              <input type="number" required min="0" placeholder="e.g., 5" value={yearsExp} onChange={e => setYearsExp(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-311" />
            </div>
          </div>

          <div data-id="element-312">
            <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-313">
              {t('specialists.form.labels.availability')}{' '}
              <span className="text-red-500" data-id="element-314">*</span>
            </label>
            <input type="date" required value={availability} onChange={e => setAvailability(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-315" />
            <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-316">
              {t('specialists.form.labels.availabilityHint')}
            </p>
          </div>

          <div className="pt-2" data-id="element-317">
            <label className="flex items-start gap-3 cursor-pointer" data-id="element-318">
              <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-[var(--color-tan)] border-gray-300 rounded focus:ring-[var(--color-tan)]" data-id="element-319" />
              <span className="text-sm text-[var(--color-text-dark)]" data-id="element-320">
                {t('specialists.form.labels.consent')}{' '}
                <span className="text-red-500" data-id="element-321">*</span>
              </span>
            </label>
          </div>

          {/* Error message */}
          {submitStatus === 'error' && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-322">
              <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-323" />
              <span data-id="element-324">{t('formStatus.error')}</span>
            </div>}

          <div className="flex justify-center pt-2" data-id="element-325">
            <button type="submit" disabled={!agreedToTerms || submitting} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-all flex items-center gap-2 ${agreedToTerms && !submitting ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-326">
              {submitting ? <>
                  <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-327" />
                  {t('formStatus.sending')}
                </> : <>
                  <CheckCircle className="w-5 h-5" data-id="element-328" />
                  {t('specialists.form.labels.submit')}
                </>}
            </button>
          </div>

          <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-329">
            {t('specialists.form.labels.disclaimer')}
          </p>
        </form>
      </div>
    </div>, document.body);
}